/**
 * ListaPacientesView — Vista densa de pacientes con búsqueda dominante,
 * chips de filtro, sort, headers alfabéticos sticky y filas compactas.
 */
import React, { useMemo } from 'react';
import {
  Box, Typography, Paper, Avatar, Chip, IconButton, Tooltip, Button,
  TextField, InputAdornment, CircularProgress, Menu, MenuItem,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Person as PersonIcon,
  Edit as EditIcon,
  ChevronRight as ChevronRightIcon,
  Clear as ClearIcon,
  SortByAlpha as SortByAlphaIcon,
  AccessTime as AccessTimeIcon,
  FiberNew as FiberNewIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';

// Paleta de avatares (8 colores estables hasheados por nombre)
const AVATAR_COLORS = [
  { bg: '#dbeafe', fg: '#1e40af' }, // azul
  { bg: '#dcfce7', fg: '#166534' }, // verde
  { bg: '#fce7f3', fg: '#9f1239' }, // rosa
  { bg: '#fef3c7', fg: '#92400e' }, // ambar
  { bg: '#ede9fe', fg: '#5b21b6' }, // morado
  { bg: '#cffafe', fg: '#155e75' }, // cyan
  { bg: '#fee2e2', fg: '#991b1b' }, // rojo
  { bg: '#e0e7ff', fg: '#3730a3' }, // indigo
];

function hashColor(name) {
  let h = 0;
  for (let i = 0; i < (name || '').length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}

function iniciales(nombre) {
  if (!nombre) return '?';
  const parts = nombre.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

function diasDesde(iso) {
  if (!iso) return null;
  return Math.floor((Date.now() - new Date(iso).getTime()) / (24 * 3600 * 1000));
}

function formatearUltimaVisita(iso) {
  if (!iso) return null;
  const d = diasDesde(iso);
  if (d === 0) return 'hoy';
  if (d === 1) return 'ayer';
  if (d < 7) return `hace ${d} días`;
  if (d < 30) return `hace ${Math.floor(d / 7)} sem`;
  if (d < 365) return `hace ${Math.floor(d / 30)} meses`;
  return `hace ${Math.floor(d / 365)} año${d >= 730 ? 's' : ''}`;
}

const SEGMENTOS = [
  { id: 'todos',       label: 'Todos' },
  { id: 'recientes',   label: 'Recientes',     hint: 'Visita en últimos 30 días' },
  { id: 'sin_visita',  label: 'Sin visita',    hint: 'Más de 6 meses sin consulta' },
  { id: 'nuevos',      label: 'Nuevos',        hint: 'Registrados en últimos 14 días' },
  { id: 'inactivos',   label: 'Inactivos' },
];

const ORDENES = [
  { id: 'alfabetico',     label: 'Alfabético',          icono: <SortByAlphaIcon sx={{ fontSize: 16 }} /> },
  { id: 'ultima_visita',  label: 'Última visita',       icono: <AccessTimeIcon  sx={{ fontSize: 16 }} /> },
  { id: 'reciente',       label: 'Más reciente',        icono: <FiberNewIcon    sx={{ fontSize: 16 }} /> },
];

export default function ListaPacientesView({
  pacientes, totalPacientes, busqueda, setBusqueda,
  filtroSegmento, setFiltroSegmento, orden, setOrden,
  loading, onNuevoPaciente, onSeleccionar, onEditar, calcularEdad,
}) {
  const [menuOrden, setMenuOrden] = React.useState(null);

  // Conteos por segmento (sobre todos los pacientes recibidos del servidor)
  const conteos = useMemo(() => {
    const c = { todos: 0, recientes: 0, sin_visita: 0, nuevos: 0, inactivos: 0 };
    for (const p of pacientes) {
      c.todos++;
      if (!p.activo) c.inactivos++;
      const dias = diasDesde(p.ultima_visita);
      if (dias !== null && dias <= 30) c.recientes++;
      if (dias !== null && dias > 180) c.sin_visita++;
      const diasReg = diasDesde(p.created_at);
      if (diasReg !== null && diasReg <= 14) c.nuevos++;
    }
    return c;
  }, [pacientes]);

  // Filtrar + ordenar
  const pacientesVisibles = useMemo(() => {
    let lista = [...pacientes];

    // Aplicar segmento
    lista = lista.filter(p => {
      if (filtroSegmento === 'todos') return true;
      if (filtroSegmento === 'inactivos') return !p.activo;
      if (filtroSegmento === 'recientes') {
        const d = diasDesde(p.ultima_visita);
        return d !== null && d <= 30;
      }
      if (filtroSegmento === 'sin_visita') {
        const d = diasDesde(p.ultima_visita);
        return d !== null && d > 180;
      }
      if (filtroSegmento === 'nuevos') {
        const d = diasDesde(p.created_at);
        return d !== null && d <= 14;
      }
      return true;
    });

    // Aplicar orden
    if (orden === 'alfabetico') {
      lista.sort((a, b) => (a.nombre_completo || '').localeCompare(b.nombre_completo || '', 'es'));
    } else if (orden === 'ultima_visita') {
      lista.sort((a, b) => {
        const da = a.ultima_visita ? new Date(a.ultima_visita).getTime() : 0;
        const db = b.ultima_visita ? new Date(b.ultima_visita).getTime() : 0;
        return db - da;
      });
    } else if (orden === 'reciente') {
      lista.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    }
    return lista;
  }, [pacientes, filtroSegmento, orden]);

  // Agrupar por letra inicial cuando es alfabético
  const grupos = useMemo(() => {
    if (orden !== 'alfabetico') return null;
    const map = new Map();
    for (const p of pacientesVisibles) {
      const letra = (p.nombre_completo || '?').charAt(0).toUpperCase();
      if (!map.has(letra)) map.set(letra, []);
      map.get(letra).push(p);
    }
    return Array.from(map.entries());
  }, [pacientesVisibles, orden]);

  const ordenActual = ORDENES.find(o => o.id === orden) || ORDENES[0];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e3a5f', mb: 0.3 }}>
            Pacientes
          </Typography>
          <Typography sx={{ fontSize: 12, color: '#64748b' }}>
            {pacientesVisibles.length} de {totalPacientes} {filtroSegmento !== 'todos' ? `(filtro: ${SEGMENTOS.find(s => s.id === filtroSegmento)?.label})` : ''}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onNuevoPaciente}
          sx={{
            background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)',
            borderRadius: 2,
            px: 2.5, py: 0.8,
            fontSize: 13, fontWeight: 700, textTransform: 'none',
            boxShadow: '0 4px 12px rgba(30,58,95,0.3)',
            '&:hover': { background: 'linear-gradient(135deg, #1e3a5f 0%, #1e4670 100%)' },
          }}
        >
          Nuevo paciente
        </Button>
      </Box>

      {/* Búsqueda dominante */}
      <TextField
        fullWidth
        autoFocus
        placeholder="Buscar por nombre, teléfono, CURP, expediente..."
        value={busqueda}
        onChange={(e) => setBusqueda(e.target.value)}
        sx={{
          mb: 1.5,
          '& .MuiOutlinedInput-root': {
            borderRadius: 2.5,
            background: '#fff',
            fontSize: 14,
            '& fieldset': { borderColor: '#e2e8f0' },
            '&:hover fieldset': { borderColor: '#cbd5e1' },
            '&.Mui-focused fieldset': { borderColor: '#1e3a5f', borderWidth: 2 },
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: '#94a3b8' }} />
            </InputAdornment>
          ),
          endAdornment: busqueda && (
            <InputAdornment position="end">
              <IconButton size="small" onClick={() => setBusqueda('')}>
                <ClearIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      {/* Toolbar: segmentos + sort */}
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        mb: 1.5, flexWrap: 'wrap', gap: 1,
      }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6, flex: 1 }}>
          {SEGMENTOS.map(s => {
            const activo = filtroSegmento === s.id;
            const count = conteos[s.id] || 0;
            return (
              <Tooltip key={s.id} title={s.hint || ''} arrow placement="top">
                <Chip
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <span>{s.label}</span>
                      <Box sx={{
                        fontSize: 10, fontWeight: 700,
                        bgcolor: activo ? 'rgba(255,255,255,0.25)' : '#e2e8f0',
                        color: activo ? '#fff' : '#475569',
                        px: 0.7, py: 0.05, borderRadius: 1,
                      }}>
                        {count}
                      </Box>
                    </Box>
                  }
                  size="small"
                  onClick={() => setFiltroSegmento(s.id)}
                  sx={{
                    fontSize: 12, fontWeight: 600, height: 28,
                    cursor: 'pointer',
                    color: activo ? '#fff' : '#475569',
                    background: activo ? 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)' : '#fff',
                    border: activo ? 'none' : '1px solid #e2e8f0',
                    '&:hover': {
                      background: activo
                        ? 'linear-gradient(135deg, #1e3a5f 0%, #1e4670 100%)'
                        : '#f8fafc',
                    },
                  }}
                />
              </Tooltip>
            );
          })}
        </Box>

        <Button
          size="small"
          onClick={(e) => setMenuOrden(e.currentTarget)}
          startIcon={ordenActual.icono}
          sx={{
            textTransform: 'none', fontSize: 12, fontWeight: 600,
            color: '#475569', borderRadius: 2,
            border: '1px solid #e2e8f0', background: '#fff',
            '&:hover': { background: '#f8fafc' },
          }}
        >
          {ordenActual.label}
        </Button>
        <Menu
          anchorEl={menuOrden} open={Boolean(menuOrden)} onClose={() => setMenuOrden(null)}
          PaperProps={{ sx: { borderRadius: 2, mt: 0.5, minWidth: 180 } }}
        >
          {ORDENES.map(o => (
            <MenuItem
              key={o.id}
              selected={o.id === orden}
              onClick={() => { setOrden(o.id); setMenuOrden(null); }}
              sx={{ fontSize: 13, gap: 1 }}
            >
              <Box sx={{ color: o.id === orden ? '#1e3a5f' : '#94a3b8' }}>{o.icono}</Box>
              {o.label}
            </MenuItem>
          ))}
        </Menu>
      </Box>

      {/* Lista */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress />
        </Box>
      ) : pacientesVisibles.length === 0 ? (
        <Paper sx={{
          p: 6, textAlign: 'center', borderRadius: 3,
          border: '1px dashed #cbd5e1', background: '#f8fafc',
        }}>
          <PersonIcon sx={{ fontSize: 56, color: '#cbd5e1', mb: 1.5 }} />
          <Typography sx={{ color: '#475569', fontSize: 14, fontWeight: 600 }}>
            {busqueda
              ? `Sin resultados para "${busqueda}"`
              : filtroSegmento !== 'todos'
                ? 'No hay pacientes en este filtro'
                : 'No hay pacientes registrados'}
          </Typography>
          {(busqueda || filtroSegmento !== 'todos') && (
            <Button
              size="small" onClick={() => { setBusqueda(''); setFiltroSegmento('todos'); }}
              sx={{ mt: 1, textTransform: 'none', fontSize: 12 }}
            >
              Limpiar filtros
            </Button>
          )}
        </Paper>
      ) : (
        <Paper sx={{
          borderRadius: 3, overflow: 'hidden',
          border: '1px solid #e2e8f0',
          background: '#fff',
        }}>
          {grupos ? (
            grupos.map(([letra, pacGrupo]) => (
              <React.Fragment key={letra}>
                <Box sx={{
                  position: 'sticky', top: 0, zIndex: 2,
                  px: 2, py: 0.6,
                  background: 'linear-gradient(180deg, #f8fafc, #f1f5f9)',
                  borderBottom: '1px solid #e2e8f0',
                  borderTop: '1px solid #e2e8f0',
                }}>
                  <Typography sx={{
                    fontSize: 11, fontWeight: 800, color: '#475569',
                    letterSpacing: 1.5,
                  }}>
                    {letra}  ·  {pacGrupo.length}
                  </Typography>
                </Box>
                {pacGrupo.map(p => (
                  <PacienteFila
                    key={p.id} paciente={p}
                    calcularEdad={calcularEdad}
                    onSeleccionar={onSeleccionar} onEditar={onEditar}
                  />
                ))}
              </React.Fragment>
            ))
          ) : (
            pacientesVisibles.map(p => (
              <PacienteFila
                key={p.id} paciente={p}
                calcularEdad={calcularEdad}
                onSeleccionar={onSeleccionar} onEditar={onEditar}
              />
            ))
          )}
        </Paper>
      )}
    </Box>
  );
}

function PacienteFila({ paciente, calcularEdad, onSeleccionar, onEditar }) {
  const color = hashColor(paciente.nombre_completo);
  const inits = iniciales(paciente.nombre_completo);
  const dias = diasDesde(paciente.ultima_visita);
  const ultimaTxt = formatearUltimaVisita(paciente.ultima_visita);
  const diasReg = diasDesde(paciente.created_at);

  // Badges contextuales
  const badges = [];
  if (!paciente.activo) {
    badges.push({ label: 'Inactivo', color: '#94a3b8', bg: '#f1f5f9' });
  }
  if (dias !== null && dias > 365) {
    badges.push({ label: 'Sin visita >1 año', color: '#b45309', bg: '#fef3c7', icon: <WarningIcon sx={{ fontSize: 11 }} /> });
  } else if (dias !== null && dias > 180) {
    badges.push({ label: 'Sin visita >6 meses', color: '#a16207', bg: '#fef9c3' });
  }
  if (diasReg !== null && diasReg <= 14) {
    badges.push({ label: 'Nuevo', color: '#15803d', bg: '#dcfce7' });
  }
  if (!paciente.numero_expediente) {
    badges.push({ label: 'Sin expediente', color: '#64748b', bg: '#f1f5f9' });
  }

  const sexoTxt = paciente.sexo ? paciente.sexo.charAt(0).toUpperCase() : '';
  const edad = calcularEdad ? calcularEdad(paciente.fecha_nacimiento) : '';

  return (
    <Box
      onClick={() => onSeleccionar(paciente)}
      sx={{
        display: 'flex', alignItems: 'center', gap: 1.2,
        px: 2, py: 1.2,
        cursor: 'pointer',
        borderBottom: '1px solid #f1f5f9',
        transition: 'all 0.15s',
        '&:last-child': { borderBottom: 'none' },
        '&:hover': {
          background: '#f8fafc',
          '& .row-actions': { opacity: 1 },
          '& .row-chevron': { transform: 'translateX(2px)', color: '#1e3a5f' },
        },
      }}
    >
      {/* Avatar */}
      <Avatar sx={{
        width: 38, height: 38,
        bgcolor: color.bg, color: color.fg,
        fontSize: 13, fontWeight: 800,
        flexShrink: 0,
      }}>
        {inits}
      </Avatar>

      {/* Info principal */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.2 }}>
          <Typography sx={{
            fontSize: 14, fontWeight: 700, color: '#1e293b',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {paciente.nombre_completo}
          </Typography>
          {badges.map((b, i) => (
            <Chip
              key={i} label={b.label} size="small"
              icon={b.icon || undefined}
              sx={{
                height: 18, fontSize: 9.5, fontWeight: 700,
                background: b.bg, color: b.color,
                '& .MuiChip-icon': { color: b.color, ml: 0.4 },
                '& .MuiChip-label': { px: 0.7 },
              }}
            />
          ))}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, color: '#64748b', fontSize: 11.5 }}>
          {(edad || sexoTxt) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
              <span>{edad ? `${edad}a` : ''}{sexoTxt ? ` · ${sexoTxt}` : ''}</span>
            </Box>
          )}
          {paciente.telefono && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
              <Box component="span" sx={{ fontFamily: 'ui-monospace, monospace', fontSize: 11 }}>
                {paciente.telefono}
              </Box>
            </Box>
          )}
          {paciente.numero_expediente && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
              <span>·</span>
              <Box component="span" sx={{ fontFamily: 'ui-monospace, monospace', fontSize: 11 }}>
                {paciente.numero_expediente}
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {/* Última visita */}
      <Box sx={{ textAlign: 'right', mr: 1, flexShrink: 0, minWidth: 90 }}>
        {ultimaTxt ? (
          <>
            <Typography sx={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>
              Últ. visita
            </Typography>
            <Typography sx={{ fontSize: 12, color: '#475569', fontWeight: 600 }}>
              {ultimaTxt}
            </Typography>
          </>
        ) : (
          <Typography sx={{ fontSize: 11, color: '#cbd5e1', fontStyle: 'italic' }}>
            Sin visitas
          </Typography>
        )}
      </Box>

      {/* Acciones (visibles en hover) */}
      <Box className="row-actions" sx={{
        display: 'flex', alignItems: 'center', gap: 0.3,
        opacity: 0, transition: 'opacity 0.15s', flexShrink: 0,
      }}>
        <Tooltip title="Editar ficha">
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); onEditar(paciente); }}
            sx={{
              color: '#6366f1', p: 0.5,
              '&:hover': { background: '#eef2ff' },
            }}
          >
            <EditIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Chevron */}
      <ChevronRightIcon
        className="row-chevron"
        sx={{
          fontSize: 18, color: '#cbd5e1', flexShrink: 0,
          transition: 'all 0.15s',
        }}
      />
    </Box>
  );
}
